import { useTranslation } from "react-i18next";
import { VideoEditingConfigType } from "../../../../models/video-editing";
import QueryService from "../../../../utils/QueryService";
import { VideoConstraints } from "../../../Playground/VideoGeneration/Error/useVideoGenerationError";

export type VideoMetadata = {
	duration: number;
	fps: number;
};

export type VideoValidationErrors = {
	duration?: string;
	fps?: string;
};

export const useVideoEditingConfig = () => {
	const { t: tErrors } = useTranslation("video_editing", { keyPrefix: "errors" });

	const validateVideoConstraints = (
		metadata: VideoMetadata,
		constraints: VideoConstraints,
	): VideoValidationErrors => {
		const errors: VideoValidationErrors = {};

		if (constraints.maxDuration && metadata.duration > constraints.maxDuration) {
			errors.duration = tErrors("durationExceeds", { maxDuration: constraints.maxDuration });
		}

		const { minFPS, maxFPS } = constraints;
		const fps = metadata.fps;

		if ((minFPS && fps < minFPS) || (maxFPS && fps > maxFPS)) {
			const rangeText =
				minFPS && maxFPS
					? tErrors("fpsRangeBetween", { minFPS, maxFPS })
					: minFPS
						? tErrors("fpsRangeHigher", { minFPS })
						: tErrors("fpsRangeLower", { maxFPS });
			errors.fps = tErrors("fpsMustBe", { rangeText, currentFPS: Math.round(fps) });
		}

		return errors;
	};

	const validateEraseConfig = (config: VideoEditingConfigType) => {
		let validated = true;
		if (!config.tool_config?.erase?.mask) {
			validated = false;
		}
		return validated;
	};

	const validateRemoveBackgroundConfig = (config: VideoEditingConfigType) => {
		let validated = true;
		if (!config.tool_config?.remove_background) {
			validated = false;
		}
		return validated;
	};

	const validateSolidBackgroundConfig = (config: VideoEditingConfigType) => {
		let validated = true;
		if (!config.tool_config?.solid_background?.background_color) {
			validated = false;
		}
		return validated;
	};

	const validateIncreaseResolutionConfig = (config: VideoEditingConfigType) => {
		let validated = true;
		if (!config.tool_config?.increase_resolution?.desired_increase) {
			validated = false;
		}
		return validated;
	};

	const validateConfig = (config: VideoEditingConfigType) => {
		if (!config.video) {
			return false;
		}
		if (!config.tool) {
			return false;
		}

		switch (config.tool) {
			case "erase":
				return validateEraseConfig(config);
			case "remove_background":
				return validateRemoveBackgroundConfig(config);
			case "solid_background":
				return validateSolidBackgroundConfig(config);
			case "increase_resolution":
				return validateIncreaseResolutionConfig(config);
			default:
				return false;
		}
	};

	const getVideoMetadata = async (videoFile: File): Promise<VideoMetadata> => {
		const queryService = new QueryService("/upload-video");
		const formData = new FormData();
		formData.append("file", videoFile);

		try {
			const response = await queryService.post("/metadata", formData, { "Content-Type": "multipart/form-data" });
			return {
				duration: response.duration,
				fps: response.fps,
			};
		} catch (error: any) {
			throw new Error(error?.response?.data?.error || error?.message || "Failed to get video metadata.");
		}
	};

	return {
		validateVideoConstraints,
		validateConfig,
		validateEraseConfig,
		validateRemoveBackgroundConfig,
		validateSolidBackgroundConfig,
		validateIncreaseResolutionConfig,
		getVideoMetadata,
	};
};
